import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import applicationService from '@/shared/services/applicationService';
import ActionBar from '@/components/common/ActionBar';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import {
    getCourseConfirmationOtherFieldErrors,
    MeetingFields,
} from './CreateApplicationSection1';
import { meetingsFromSaved } from './meetings';

/**
 * "Update meeting times and room": the submitter changes the section's
 * meetings after submit, even after a program has decided
 * (PUT /api/application/:id/meetings). Only the meetings are offered; the
 * enrollment, modality and program answers stay with the coordinators. The
 * request keeps its status. A change to days or times is checked first for
 * the students it reaches. onSaved gets the server's answer.
 */

// The statuses the server refuses: the whole form covers a draft or a
// request in revision, and a withdrawn one is reopened first
export const MEETINGS_LOCKED = ['draft', 'revision_requested', 'withdrawn'];

// Who hears of a change besides any placed students: on a request filed for
// someone else, the instructor of record as well as the coordinators
export const toldOf = (application) =>
    application.isSubmittedOnBehalf
        ? 'The program coordinators and the instructor of record'
        : 'The program coordinators';

const differs = (a, b) => JSON.stringify(a) !== JSON.stringify(b);

const withoutRoom = (meetings) =>
    meetings.map((meeting) => ({ ...meeting, room: undefined }));

function UpdateMeetingsButton({ application, onSaved }) {
    const [show, setShow] = useState(false);
    const [initial, setInitial] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [attempted, setAttempted] = useState(false);
    // { placed } while the dialog shows who a time change reaches
    const [warning, setWarning] = useState(null);
    const [saved, setSaved] = useState(null);
    const errorRef = useRevealAlert(error);
    const warningRef = useRevealAlert(warning);

    if (MEETINGS_LOCKED.includes(application.status)) return null;

    const courseLabel =
        `${application.course?.subject ?? ''} ${application.course?.courseNumber ?? ''}`.trim();
    const titleId = `update-meetings-title-${application._id}`;
    const firstUntyped = !application.courseConfirmation?.meetings?.[0]?.type;
    const fieldErrors = getCourseConfirmationOtherFieldErrors(
        { modality: '', otherModality: '', meetings },
        'um',
        { required: true, firstUntyped }
    );
    const hasError = (id) => attempted && fieldErrors.some((e) => e.id === id);
    const told = toldOf(application);

    const open = () => {
        const start = meetingsFromSaved(application.courseConfirmation);
        setInitial(start);
        setMeetings(start);
        setError(null);
        setAttempted(false);
        setWarning(null);
        setSaved(null);
        setShow(true);
    };

    const close = () => {
        if (!saving) setShow(false);
    };

    const submit = async () => {
        setSaving(true);
        try {
            setSaved(
                await applicationService.updateMeetings(
                    application._id,
                    meetings
                )
            );
            setShow(false);
        } catch (err) {
            setWarning(null);
            setError(
                err.response?.data?.message ||
                    'The changes were not saved. Please try again.'
            );
        } finally {
            setSaving(false);
        }
    };

    const save = async (event) => {
        event.preventDefault();
        setError(null);
        setAttempted(true);
        if (fieldErrors.length > 0) {
            setError(`Missing: ${fieldErrors.map((e) => e.label).join(', ')}.`);
            return;
        }
        if (!differs(initial, meetings)) {
            setShow(false);
            return;
        }
        const timesChanged = differs(
            withoutRoom(initial),
            withoutRoom(meetings)
        );
        if (!warning && timesChanged) {
            setSaving(true);
            let impact = null;
            try {
                impact = await applicationService.meetingsImpact(
                    application._id
                );
            } catch {
                impact = null;
            }
            setSaving(false);
            if (!impact || impact.placed > 0) {
                setWarning({ placed: impact ? impact.placed : null });
                return;
            }
        }
        await submit();
    };

    return (
        <>
            <Button
                variant="outline-primary"
                onClick={open}
                aria-label={`Update meeting times and room for ${courseLabel || 'this course'}`}
            >
                Update meeting times and room
            </Button>
            <Modal
                show={show}
                onHide={close}
                onExited={() => {
                    if (saved) {
                        onSaved(saved);
                        setSaved(null);
                    }
                }}
                restoreFocus={!saved}
                size="lg"
                fullscreen="sm-down"
                scrollable
                aria-labelledby={titleId}
            >
                <Form
                    noValidate
                    onSubmit={save}
                    className="d-flex flex-column flex-grow-1 overflow-hidden"
                >
                    <Modal.Header closeButton>
                        <Modal.Title id={titleId} as="h2" className="h5">
                            Update meeting times and room
                            {courseLabel ? `: ${courseLabel}` : ''}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {warning ? (
                            <Alert
                                ref={warningRef}
                                tabIndex={-1}
                                variant="warning"
                                className="mb-0"
                            >
                                <h3 className="h6">
                                    Check who this reaches before you save
                                </h3>
                                <p className="mb-0">
                                    {warning.placed === null
                                        ? "We couldn't check which students this reaches. Go back and try again, or save anyway."
                                        : `You changed the days or times. ${warning.placed} ${warning.placed === 1 ? 'student is' : 'students are'} already placed in this section and will be emailed the new schedule. ${told} are told too.`}
                                </p>
                            </Alert>
                        ) : (
                            <>
                                <p>
                                    Your request keeps its status and no
                                    program&apos;s review stops. {told} are told
                                    what changed. To change the enrollment,
                                    modality or a program answer, ask a
                                    coordinator.
                                </p>
                                <MeetingFields
                                    meetings={meetings}
                                    onChange={setMeetings}
                                    hasError={hasError}
                                    idPrefix="um"
                                    inDialog
                                />
                            </>
                        )}
                        {error && (
                            <Alert
                                ref={errorRef}
                                tabIndex={-1}
                                variant="danger"
                                className="mb-0 mt-3"
                            >
                                {error}
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <ActionBar
                            className="w-100"
                            secondary={
                                warning ? (
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setWarning(null)}
                                        disabled={saving}
                                    >
                                        Go back
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outline-primary"
                                        onClick={close}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </Button>
                                )
                            }
                            primary={
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <Spinner
                                                animation="border"
                                                size="sm"
                                                className="me-2"
                                                aria-hidden="true"
                                            />
                                            Saving…
                                        </>
                                    ) : warning ? (
                                        'Save anyway'
                                    ) : (
                                        'Save changes'
                                    )}
                                </Button>
                            }
                        />
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
}

UpdateMeetingsButton.propTypes = {
    application: PropTypes.shape({
        _id: PropTypes.string,
        status: PropTypes.string,
        isSubmittedOnBehalf: PropTypes.bool,
        course: PropTypes.object,
        courseConfirmation: PropTypes.object,
    }).isRequired,
    onSaved: PropTypes.func.isRequired,
};

export default UpdateMeetingsButton;
